import { z } from 'zod'

export const problemSchema = z.object({
  type: z.string().optional(),
  title: z.string().optional(),
  status: z.number().optional(),
  detail: z.string().optional(),
  instance: z.string().optional(),
})

export type Problem = z.infer<typeof problemSchema>

export const problemTypes = {
  buildNotAvailable: 'urn:partner-space:build-not-available',
  partnerValidationFailed: 'urn:partner-space:partner-validation-failed',
  termsNotAccepted: 'urn:partner-space:terms-not-accepted',
  termsVersionMismatch: 'urn:partner-space:terms-version-mismatch',
} as const

export const parseProblem = (body: unknown): Problem | null => {
  const parsed = problemSchema.safeParse(body)
  return parsed.success ? parsed.data : null
}

export class ApiError extends Error {
  readonly status: number
  readonly problem: Problem | null
  readonly correlationId: string | null

  constructor(status: number, problem: Problem | null, correlationId: string | null) {
    super(problem?.detail ?? problem?.title ?? `Request failed with status ${String(status)}`)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
    this.correlationId = correlationId
  }

  get type(): string {
    return this.problem?.type ?? 'about:blank'
  }

  get isBuildNotAvailable(): boolean {
    return this.type === problemTypes.buildNotAvailable
  }

  get isPartnerValidationFailed(): boolean {
    return this.type === problemTypes.partnerValidationFailed
  }

  get isTermsNotAccepted(): boolean {
    return this.type === problemTypes.termsNotAccepted
  }

  get isTermsVersionMismatch(): boolean {
    return this.type === problemTypes.termsVersionMismatch
  }

  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('The request never reached the server')
    this.name = 'NetworkError'
    this.cause = cause
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError
